import React from "react";
import styled from "styled-components";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faEnvelope,
  faLock,
  faUserPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ProfilePage.scss";
import { EarnStickersTab } from "./awards_tabs/EarnStickersTab";
import { LeaderboardsTab } from "./awards_tabs/LeaderboardsTab";
import {
  CoursesModal,
  StreakModal,
} from "../components/profile/ProfileStatModals";
import {
  STAT_ICONS,
  StatIcon,
  StatIconStyle,
} from "../components/profile/ProfileIcons";
import {
  MilestonesResponse,
  User,
  isSchoolUser,
  isUserExpired,
  isUserSignedUp,
  nonGuestProfiles,
} from "../common/api";
import {
  Account,
  AccountContext,
  MAX_PROFILES_PER_USER,
  addProfilePath,
  isProfileManagementUnlocked,
} from "../common/context/AccountContext";
import { ApiContext } from "../common/context/ApiContext";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import {
  AnalyticsContext,
  LoginWithClassLinkButtonTapEvent,
  LoginWithCleverButtonTapEvent,
  MenuAddProfileButtonTapEvent,
  MenuLogInButtonTapEvent,
  MenuSelectProfileButtonTapEvent,
  MenuUpgradeButtonTapEvent,
  ViewAwardsEvent,
} from "../common/context/AnalyticsContext";
import { useLiveData } from "../common/utils";
import {
  PrimaryButton,
  SchoolLoginButton,
  SchoolLoginButtonIconImage,
} from "../common/CommonStyles";
import {
  COLOR_ELEVATION_SHADOW,
  COLOR_NEUTRAL_BORDER,
  COLOR_RED,
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_TERTIARY,
  COLOR_WHITE,
} from "../common/colors";
import {
  SPACE_SM,
  SPACE_MD,
  SPACE_LG,
  SPACE_XL,
  SPACE_2XL,
} from "../common/spacing";
import { GENERIC_ITEM_BORDER_RADIUS } from "../common/constants";

const enum StickersSectionKey {
  Stickers = "STICKERS",
  Leaderboard = "LEADERBOARD",
}

const enum ProfileBanner {
  NoAccount,
  NoProfile,
  Expired,
}

const EMPTY_MILESTONES: MilestonesResponse = {
  completed_lesson_count: 0,
  completed_unit_count: 0,
  current_weekly_lesson_completion_streak_count: 0,
  highest_weekly_lesson_completion_streak_count: 0,
  units_progress: [],
};

const profileBanner = (user: User) => {
  if (isUserExpired(user)) {
    return ProfileBanner.Expired;
  }
  if (!isUserSignedUp(user)) {
    return ProfileBanner.NoAccount;
  }
  if (nonGuestProfiles(user).length === 0) {
    return ProfileBanner.NoProfile;
  }
  return null;
};

const NameHeading = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 600;
  line-height: 48px;
  letter-spacing: -0.15px;
  color: ${COLOR_TEXT_PRIMARY};
`;

const NameMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  padding: 0;
  background: none;
  border: none;
  color: ${COLOR_TEXT_PRIMARY};
`;

const StatsRow = styled.div.attrs({ className: "profile-stats-row" })`
  --stat-card-min-width: 190px;
  --stat-card-max-width: 240px;
  --stat-gap: ${SPACE_SM}px;

  display: flex;
  flex-wrap: wrap;
  column-gap: var(--stat-gap);
  row-gap: ${SPACE_MD}px;
  max-width: calc(4 * var(--stat-card-max-width) + 3 * var(--stat-gap));
  padding-top: ${SPACE_XL}px;
`;

const StatPair = styled.div.attrs({ className: "profile-stat-pair" })`
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 calc(2 * var(--stat-card-min-width) + var(--stat-gap));
  gap: var(--stat-gap);
`;

const StatCardBox = styled.div`
  flex: 1 1 var(--stat-card-min-width);
  min-width: var(--stat-card-min-width);
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  height: 56px;
  padding: ${SPACE_MD}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_WHITE};
  border: 1px solid ${COLOR_NEUTRAL_BORDER};
  box-shadow: 0 1px 2px 1px ${COLOR_ELEVATION_SHADOW};
  font: inherit;
  text-align: start;

  &[type="button"] {
    cursor: pointer;
  }
`;

const StatChevron = styled(FontAwesomeIcon)`
  margin-left: auto;
  color: ${COLOR_TEXT_TERTIARY};
`;

const StatValue = styled.div<{ $isEmpty: boolean }>`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.15px;
  color: ${(props) =>
    props.$isEmpty ? COLOR_TEXT_TERTIARY : COLOR_TEXT_PRIMARY};
`;

const StatCaption = styled.div`
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: -0.2px;
  color: ${COLOR_TEXT_TERTIARY};
`;

const Rule = styled.hr`
  margin: ${SPACE_XL}px 0;
  border: none;
  border-top: 1px solid ${COLOR_NEUTRAL_BORDER};
  opacity: 1;
`;

const SectionHeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  padding: ${SPACE_MD}px 0;
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${COLOR_TEXT_PRIMARY};
`;

const CountBadge = styled.span`
  min-width: 30px;
  padding: 2px 4px;
  border: 1px solid ${COLOR_NEUTRAL_BORDER};
  border-radius: 1000px;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  color: ${COLOR_TEXT_SECONDARY};
`;

const SectionToggle = styled(ToggleButtonGroup)`
  margin-left: auto;

  .MuiToggleButton-root {
    padding: 4px 14px;
    border-radius: 25px;
    text-transform: none;
    font-size: 14px;
    color: ${COLOR_TEXT_SECONDARY};
  }
  .MuiToggleButton-root.Mui-selected {
    background-color: ${COLOR_SURFACE_CONTAINER};
    color: ${COLOR_TEXT_PRIMARY};
  }
`;

const AccountCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE_LG}px;
  max-width: 480px;
  margin: 0 auto ${SPACE_2XL}px;
  padding: ${SPACE_XL}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_WHITE};
  box-shadow:
    0 1px 2px 1px ${COLOR_ELEVATION_SHADOW},
    inset 0 0 0 1px ${COLOR_ELEVATION_SHADOW};
  text-align: center;
`;

const AccountCardTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: ${COLOR_TEXT_PRIMARY};
`;

const AccountCardButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_SM}px;
  width: 300px;
  max-width: 100%;

  > button {
    width: 100%;
    margin: 0;
  }
`;

const SchoolLoginButtonIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
`;

const StatCard: React.FC<{
  icon: StatIconStyle;
  isEmpty: boolean;
  value: string;
  caption: string;
  hasChevron?: boolean;
  onClick?: () => void;
}> = ({ icon, isEmpty, value, caption, hasChevron, onClick }) => (
  <StatCardBox
    as={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
  >
    <StatIcon icon={icon} isEmpty={isEmpty} fixedWidth fontSize={25.6} />
    <div>
      <StatValue $isEmpty={isEmpty}>{value}</StatValue>
      <StatCaption>{caption}</StatCaption>
    </div>
    {hasChevron && <StatChevron icon={faChevronRight} />}
  </StatCardBox>
);

// Student name with a profile switcher, replacing the old navbar account menu.
const ProfileNameHeader: React.FC<{ account: Account; name: string }> = ({
  account,
  name,
}) => {
  const history = useHistory();
  const { updateUser } = React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const realProfiles = nonGuestProfiles(account.user);
  const profiles = account.user.has_given_verified_consent ? realProfiles : [];
  const canAddProfile = realProfiles.length < MAX_PROFILES_PER_USER;
  const isUnlocked = isProfileManagementUnlocked(account.user);
  // Hide for school users, and when there's nothing to switch to or add.
  const showSwitcher =
    !isSchoolUser(account.user) && (profiles.length > 1 || canAddProfile);

  if (!showSwitcher) {
    return <NameHeading>{name}</NameHeading>;
  }

  return (
    <>
      <NameMenuButton
        ref={anchorRef}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-haspopup="menu"
      >
        <NameHeading>{name}</NameHeading>
        <FontAwesomeIcon icon={faChevronDown} />
      </NameMenuButton>
      <Menu
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocusItem
        slotProps={{ paper: { style: { width: 250, marginTop: 6 } } }}
      >
        {profiles.map((profile) => (
          <MenuItem
            key={profile.id}
            onClick={() => {
              logEvent(new MenuSelectProfileButtonTapEvent(profile.id));
              setIsMenuOpen(false);
              if (isUserExpired(account.user)) {
                history.push("/account/upgrade/");
              } else if (account.profile?.id !== profile.id) {
                updateUser(account.user, false, profile.id, false);
              }
            }}
          >
            <ListItemText>{profile.first_name}</ListItemText>
            {profile.id === account.profile?.id && (
              <ListItemIcon>
                <FontAwesomeIcon icon={faCheck} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
        {canAddProfile && (
          <MenuItem
            onClick={() => {
              logEvent(new MenuAddProfileButtonTapEvent());
              setIsMenuOpen(false);
              history.push(addProfilePath(account.user));
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={isUnlocked ? faUserPlus : faLock} />
            </ListItemIcon>
            <ListItemText>{t("navigation_add_profile")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// Progress for the displayed profile, or all zeros when there is none.
const useMilestones = (account: Account | undefined) => {
  const { getMilestonesMetadata } = React.useContext(ApiContext);
  const [milestones, setMilestones] =
    React.useState<MilestonesResponse | null>(null);

  const fetchedForProfileStateVersion = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!account) {
      return;
    }
    const profile = account.displayProfile();
    if (profile === null) {
      fetchedForProfileStateVersion.current = null;
      setMilestones(EMPTY_MILESTONES);
      return;
    }
    if (
      !account.hasMaybeProfileState() ||
      account.profileStateCurrentVersion ===
        fetchedForProfileStateVersion.current
    ) {
      return;
    }
    const version = account.profileStateCurrentVersion;
    fetchedForProfileStateVersion.current = version;
    getMilestonesMetadata(profile.id).then((response) => {
      if (fetchedForProfileStateVersion.current === version) {
        setMilestones(response);
      }
    });
  }, [account, getMilestonesMetadata]);

  return milestones;
};

const ProfileStats: React.FC<{
  milestones: MilestonesResponse;
  gemBalance: number;
  hasChevrons: boolean;
}> = ({ milestones, gemBalance, hasChevrons }) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = React.useState<"streak" | "courses">();
  const closeModal = () => setOpenModal(undefined);
  const streakCount = milestones.highest_weekly_lesson_completion_streak_count;
  const courseCount = milestones.completed_unit_count;
  const lessonCount = milestones.completed_lesson_count;

  return (
    <StatsRow>
      <StatPair>
        <StatCard
          icon={STAT_ICONS.streak}
          isEmpty={streakCount === 0}
          value={t("milestones_streak_count", { count: streakCount })}
          caption={t("profile_stat_streak_caption")}
          hasChevron={hasChevrons}
          onClick={() => setOpenModal("streak")}
        />
        <StatCard
          icon={STAT_ICONS.courses}
          isEmpty={courseCount === 0}
          value={t("profile_stat_courses_value", { count: courseCount })}
          caption={t("profile_stat_completed_caption")}
          hasChevron={hasChevrons}
          onClick={() => setOpenModal("courses")}
        />
      </StatPair>
      <StatPair>
        <StatCard
          icon={STAT_ICONS.lessons}
          isEmpty={lessonCount === 0}
          value={t("profile_stat_lessons_value", { count: lessonCount })}
          caption={t("profile_stat_completed_caption")}
        />
        <StatCard
          icon={STAT_ICONS.gems}
          isEmpty={gemBalance === 0}
          value={t("profile_stat_gems_value", { count: gemBalance })}
          caption={t("profile_stat_gems_caption")}
        />
      </StatPair>
      <StreakModal
        show={openModal === "streak"}
        onHide={closeModal}
        milestones={milestones}
      />
      <CoursesModal
        show={openModal === "courses"}
        onHide={closeModal}
        milestones={milestones}
      />
    </StatsRow>
  );
};

const AccountStatusCard: React.FC<{ banner: ProfileBanner; user: User }> = ({
  banner,
  user,
}) => {
  const history = useHistory();
  const { loginWithClever, loginWithClassLink } =
    React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { t } = useTranslation();

  if (banner === ProfileBanner.Expired) {
    return (
      <AccountCard>
        <FontAwesomeIcon
          icon={faCircleExclamation}
          fontSize={32}
          color={COLOR_RED}
        />
        <AccountCardTitle>{t("profile_subscription_expired")}</AccountCardTitle>
        <AccountCardButtons>
          <PrimaryButton onClick={() => history.push("/account/manage/")}>
            {t("manage_account_manage_subscription")}
          </PrimaryButton>
        </AccountCardButtons>
      </AccountCard>
    );
  }

  const isUnlocked = isProfileManagementUnlocked(user);
  return (
    <AccountCard>
      <img
        alt=""
        src={`${process.env.PUBLIC_URL}/teacher_wave_plain.png`}
        height={120}
      />
      <AccountCardTitle>{t("profile_upsell_message")}</AccountCardTitle>
      {banner === ProfileBanner.NoAccount ? (
        <AccountCardButtons>
          <PrimaryButton
            onClick={() => {
              logEvent(new MenuUpgradeButtonTapEvent());
              history.push("/account/upgrade/");
            }}
          >
            {t("profile_create_account")}
          </PrimaryButton>
          <SchoolLoginButton
            onClick={() => {
              logEvent(new LoginWithCleverButtonTapEvent());
              loginWithClever();
            }}
          >
            <SchoolLoginButtonIconImage
              alt=""
              src={`${process.env.PUBLIC_URL}/clever.png`}
            />
            {t("profile_login_with_provider", { provider: "Clever" })}
          </SchoolLoginButton>
          <SchoolLoginButton
            onClick={() => {
              logEvent(new LoginWithClassLinkButtonTapEvent());
              loginWithClassLink();
            }}
          >
            <SchoolLoginButtonIconImage
              alt=""
              src={`${process.env.PUBLIC_URL}/classlink.png`}
            />
            {t("profile_login_with_provider", { provider: "ClassLink" })}
          </SchoolLoginButton>
          <SchoolLoginButton
            onClick={() => {
              logEvent(new MenuLogInButtonTapEvent());
              history.push("/account/login/");
            }}
          >
            <SchoolLoginButtonIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </SchoolLoginButtonIcon>
            {t("profile_login_with_email")}
          </SchoolLoginButton>
        </AccountCardButtons>
      ) : (
        <AccountCardButtons>
          <PrimaryButton
            onClick={() => {
              logEvent(new MenuAddProfileButtonTapEvent());
              history.push(addProfilePath(user));
            }}
          >
            {!isUnlocked && (
              <FontAwesomeIcon icon={faLock} className="me-2" />
            )}
            {t("profile_create_profile")}
          </PrimaryButton>
        </AccountCardButtons>
      )}
    </AccountCard>
  );
};

export const ProfilePage: React.FC = () => {
  const { setCurrentTab } = React.useContext(TabsContext);
  const { accountLiveData } = React.useContext(AccountContext);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { t } = useTranslation();
  const [account, setAccount] = React.useState<Account>();
  const [stickerCount, setStickerCount] = React.useState<number>();
  const [sectionKey, setSectionKey] = React.useState(
    StickersSectionKey.Stickers
  );
  const milestones = useMilestones(account);

  React.useEffect(() => {
    logEvent(new ViewAwardsEvent());
  }, [logEvent]);

  React.useEffect(() => {
    setCurrentTab(TabEnum.Profile);
  }, [setCurrentTab]);

  useLiveData(
    React.useCallback((account: Account) => {
      if (account.hasUserAndMaybeProfile()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  if (!account) {
    return <></>;
  }

  const banner = profileBanner(account.user);
  const displayProfile = account.displayProfile();
  const name = displayProfile?.first_name.trim() || null;
  const gemBalance = displayProfile?.gem_balance ?? 0;
  const isEmpty =
    milestones !== null &&
    stickerCount === 0 &&
    gemBalance === 0 &&
    milestones.completed_lesson_count === 0 &&
    milestones.completed_unit_count === 0 &&
    milestones.highest_weekly_lesson_completion_streak_count === 0 &&
    !milestones.units_progress.some(
      (unit) => unit.started_lesson_count > 0 || unit.completed_lesson_count > 0
    );

  const showLeaderboard =
    isSchoolUser(account.user) &&
    sectionKey === StickersSectionKey.Leaderboard;

  return (
    <div>
      {banner !== null && (
        <AccountStatusCard banner={banner} user={account.user} />
      )}
      {name && <ProfileNameHeader account={account} name={name} />}
      <ProfileStats
        milestones={milestones ?? EMPTY_MILESTONES}
        gemBalance={gemBalance}
        hasChevrons={!isEmpty}
      />
      <Rule />
      <SectionHeadingRow>
        {!showLeaderboard && (
          <>
            <SectionHeading>{t("profile_stickers")}</SectionHeading>
            {stickerCount !== undefined && (
              <CountBadge>{stickerCount}</CountBadge>
            )}
          </>
        )}
        {isSchoolUser(account.user) && (
          <SectionToggle
            exclusive
            size="small"
            value={sectionKey}
            onChange={(_, value) => {
              if (value !== null) {
                setSectionKey(value as StickersSectionKey);
              }
            }}
          >
            <ToggleButton value={StickersSectionKey.Stickers}>
              {t("profile_stickers")}
            </ToggleButton>
            <ToggleButton value={StickersSectionKey.Leaderboard}>
              {t("profile_leaderboard")}
            </ToggleButton>
          </SectionToggle>
        )}
      </SectionHeadingRow>
      {isSchoolUser(account.user) && (
        <div hidden={!showLeaderboard}>
          <LeaderboardsTab />
        </div>
      )}
      <div hidden={showLeaderboard}>
        <EarnStickersTab
          onCountChange={setStickerCount}
          showEmptyMessage={isEmpty}
        />
      </div>
    </div>
  );
};
